import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import { IoMdArrowBack } from "react-icons/io";
import {
  FiCheckCircle,
  FiDownload,
  FiEye,
  FiRefreshCw,
  FiSave,
  FiTrash2,
  FiUpload,
} from "react-icons/fi";
import API_URL from "../Config";
import "../forms/UserForm.css";
import "./miIndex.css";
import {
  MI_AFS_CUSTOMER_OPTIONS,
  MI_AFS_SALES_VOLUME_OPTIONS,
  MI_DEPOT_CAPACITY_OPTIONS,
  MI_DEPOT_OPERATION_OPTIONS,
  MI_ZONE_OPTIONS,
  buildComplexityPayload,
  buildMiBatchScorePayload,
  buildMiSingleScorePayload,
  calculateComplexityPreview,
  calculateNegativeMarkingPreview,
  createNegativeMarkingDraft,
  createPendingMiAttachment,
  downloadMiAttachment,
  fetchMiEvaluationCategories,
  formatMiDate,
  formatMiDateTime,
  formatMiNumber,
  formatMiPercent,
  getComplexityStateFromApi,
  getMiAttachmentName,
  getMiAttachmentSize,
  getMiScoreValue,
  itemHasScoreContent,
  openMiAttachment,
  parseMiApiError,
} from "./miIndexUtils";

const INITIAL_EVALUATION = {
  evaluation_id: "",
  location_id: "",
  location_name: "",
  location_type: "",
  region: "",
  evaluation_date: "",
  evaluator_name: "",
  evaluator_designation: "",
  evaluation_type: "",
  remarks: "",
  status: "Draft",
  total_marks_obtained: 0,
  total_max_marks: 97,
  percentage_obtained: 0,
  complexity_factor: 0,
  negative_marks_deducted: 0,
  final_mi_score: 0,
  created_at: "",
  updated_at: "",
};

function EditMIEvaluation({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const fileInputRefs = useRef({});

  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [categories, setCategories] = useState([]);
  const [evaluationItems, setEvaluationItems] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [evaluationData, setEvaluationData] = useState(INITIAL_EVALUATION);
  const [scores, setScores] = useState({});
  const [attachments, setAttachments] = useState({});
  const [negativeMarkingDraft, setNegativeMarkingDraft] = useState(
    createNegativeMarkingDraft(null),
  );
  const [negativeMarkingSnapshot, setNegativeMarkingSnapshot] = useState(null);
  const [complexityDraft, setComplexityDraft] = useState({
    capacity: "",
    zone: "",
    age_over_25: false,
    operations: [],
    sales_volume: "",
    customers: [],
    remarks: "",
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const isApproved = evaluationData.status === "Approved";
  const locationType = evaluationData.location_type || "";

  const totalItemCount = useMemo(
    () =>
      Object.values(evaluationItems).reduce(
        (total, items) => total + (items?.length || 0),
        0,
      ),
    [evaluationItems],
  );

  const scoredItemCount = useMemo(() => {
    let total = 0;
    Object.values(evaluationItems).forEach((items) => {
      (items || []).forEach((item) => {
        if (
          itemHasScoreContent(scores[item.item_id], attachments[item.item_id])
        ) {
          total += 1;
        }
      });
    });
    return total;
  }, [attachments, evaluationItems, scores]);

  const scoringProgress = useMemo(() => {
    if (totalItemCount === 0) return 0;
    return Math.min((scoredItemCount / totalItemCount) * 100, 100);
  }, [scoredItemCount, totalItemCount]);

  const negativePreview = useMemo(
    () => calculateNegativeMarkingPreview(negativeMarkingDraft),
    [negativeMarkingDraft],
  );

  const complexityPreview = useMemo(
    () => calculateComplexityPreview(locationType, complexityDraft),
    [complexityDraft, locationType],
  );

  const loadWorkflowData = useCallback(
    async (options = {}) => {
      const { silent = false, preserveExpanded = true } = options;
      try {
        if (!silent) {
          setLoading(true);
        }

        const [categoryData, evaluationResponse] = await Promise.all([
          fetchMiEvaluationCategories(),
          axios.get(`${API_URL}/api/mi/evaluations/${id}`, {
            headers: {
              Authorization: localStorage.getItem("authKey") || "",
            },
          }),
        ]);

        const evaluationPayload = evaluationResponse.data || {};
        const evaluation = evaluationPayload.evaluation || INITIAL_EVALUATION;
        const scoredCategories = evaluationPayload.categories || [];
        const scoredItemMap = {};

        scoredCategories.forEach((category) => {
          (category.items || []).forEach((item) => {
            scoredItemMap[item.item_id] = item;
          });
        });

        const nextScores = {};
        const nextAttachments = {};

        Object.values(categoryData.itemsByCategory).forEach((items) => {
          (items || []).forEach((item) => {
            const existingItem = scoredItemMap[item.item_id];
            nextScores[item.item_id] = {
              score_id: existingItem?.score_id || "",
              allotted_mark:
                existingItem?.allotted_mark !== null &&
                existingItem?.allotted_mark !== undefined
                  ? String(existingItem.allotted_mark)
                  : "",
              subcategory_scores: existingItem?.subcategory_scores || {},
              observations: existingItem?.observations || "",
            };
            nextAttachments[item.item_id] = Array.isArray(
              existingItem?.attachments,
            )
              ? existingItem.attachments.map((attachment) => ({
                  ...attachment,
                  isNew: false,
                }))
              : [];
          });
        });

        setCategories(categoryData.categories || []);
        setEvaluationItems(categoryData.itemsByCategory || {});
        setEvaluationData({
          ...INITIAL_EVALUATION,
          ...evaluation,
        });
        setScores(nextScores);
        setAttachments(nextAttachments);
        setNegativeMarkingSnapshot(evaluationPayload.negative_marking || null);
        setNegativeMarkingDraft(
          createNegativeMarkingDraft(
            evaluationPayload.negative_marking || null,
          ),
        );
        setComplexityDraft(
          getComplexityStateFromApi(
            evaluation.location_type,
            evaluationPayload.complexity_factor,
          ),
        );

        const defaultExpanded = {};
        (categoryData.categories || []).forEach((category, index) => {
          defaultExpanded[category.category_id] = index === 0;
        });

        setExpandedCategories((previousState) => {
          if (!preserveExpanded) {
            return defaultExpanded;
          }
          return Object.keys(previousState).length === 0
            ? defaultExpanded
            : previousState;
        });
      } catch (error) {
        showSnackbar(
          parseMiApiError(error, "Failed to load evaluation workflow"),
          "error",
        );
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [id, showSnackbar],
  );

  useEffect(() => {
    loadWorkflowData({ preserveExpanded: false });
  }, [loadWorkflowData]);

  const recalculateEvaluation = useCallback(
    async (silent = false) => {
      try {
        if (!silent) {
          setSavingKey("recalculate");
        }
        const response = await axios.post(
          `${API_URL}/api/mi/evaluations/${id}/calculate`,
          {},
          {
            headers: {
              Authorization: localStorage.getItem("authKey") || "",
            },
          },
        );
        if (!silent) {
          showSnackbar(response.data?.message || "MI score calculated");
        }
        return true;
      } catch (error) {
        showSnackbar(
          parseMiApiError(error, "Failed to calculate MI score"),
          "error",
        );
        return false;
      } finally {
        if (!silent) {
          setSavingKey("");
        }
      }
    },
    [id, showSnackbar],
  );

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prevState) => ({
      ...prevState,
      [categoryId]: !prevState[categoryId],
    }));
  };

  const handleScoreFieldChange = (itemId, field, value) => {
    setScores((prevState) => ({
      ...prevState,
      [itemId]: {
        ...prevState[itemId],
        [field]: value,
      },
    }));
  };

  const handleSubcategoryScoreChange = (itemId, subcategoryName, value) => {
    setScores((prevState) => ({
      ...prevState,
      [itemId]: {
        ...prevState[itemId],
        subcategory_scores: {
          ...(prevState[itemId]?.subcategory_scores || {}),
          [subcategoryName]: value,
        },
      },
    }));
  };

  const handleFileSelection = (itemId, fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    setAttachments((prevState) => ({
      ...prevState,
      [itemId]: [
        ...(prevState[itemId] || []),
        ...files.map(createPendingMiAttachment),
      ],
    }));

    if (fileInputRefs.current[itemId]) {
      fileInputRefs.current[itemId].value = "";
    }
  };

  const handleRemoveAttachment = async (itemId, attachment) => {
    if (attachment.isNew) {
      setAttachments((prevState) => ({
        ...prevState,
        [itemId]: (prevState[itemId] || []).filter(
          (item) => item.client_id !== attachment.client_id,
        ),
      }));
      return;
    }

    try {
      setSavingKey(`delete-attachment-${attachment.id}`);
      const response = await axios.delete(
        `${API_URL}/api/mi/attachments/${attachment.id}`,
        {
          headers: {
            Authorization: localStorage.getItem("authKey") || "",
          },
        },
      );

      setAttachments((prevState) => ({
        ...prevState,
        [itemId]: (prevState[itemId] || []).filter(
          (item) => item.id !== attachment.id,
        ),
      }));
      showSnackbar(response.data?.message || "Attachment deleted successfully");
    } catch (error) {
      showSnackbar(
        parseMiApiError(error, "Failed to delete attachment"),
        "error",
      );
    } finally {
      setSavingKey("");
    }
  };

  const saveItemScore = async (itemId, categoryId) => {
    const itemScore = scores[itemId] || {};
    const itemAttachments = attachments[itemId] || [];

    if (!itemHasScoreContent(itemScore, itemAttachments)) {
      showSnackbar(
        "Add a score, observation, or attachment before saving.",
        "warning",
      );
      return;
    }

    try {
      setSavingKey(`item-${itemId}`);
      const payload = buildMiSingleScorePayload(
        itemId,
        categoryId,
        itemScore,
        itemAttachments,
      );
      const response = await axios.post(
        `${API_URL}/api/mi/evaluations/${id}/scores`,
        payload,
        {
          headers: {
            Authorization: localStorage.getItem("authKey") || "",
            "Content-Type": "multipart/form-data",
          },
        },
      );

      await recalculateEvaluation(true);
      await loadWorkflowData({ silent: true, preserveExpanded: true });
      showSnackbar(response.data?.message || "Score saved successfully");
    } catch (error) {
      showSnackbar(parseMiApiError(error, "Failed to save score"), "error");
    } finally {
      setSavingKey("");
    }
  };

  const saveCategoryScores = async (categoryId) => {
    const categoryItems = (evaluationItems[categoryId] || []).filter((item) =>
      itemHasScoreContent(scores[item.item_id], attachments[item.item_id]),
    );

    if (categoryItems.length === 0) {
      showSnackbar("No score data found in this category.", "warning");
      return;
    }

    try {
      setSavingKey(`category-${categoryId}`);
      const payload = buildMiBatchScorePayload(
        categoryItems.map((item) => ({
          item_id: item.item_id,
          category_id: categoryId,
          score: scores[item.item_id] || {},
          attachments: attachments[item.item_id] || [],
        })),
      );

      const response = await axios.post(
        `${API_URL}/api/mi/evaluations/${id}/scores/batch`,
        payload,
        {
          headers: {
            Authorization: localStorage.getItem("authKey") || "",
            "Content-Type": "multipart/form-data",
          },
        },
      );

      await recalculateEvaluation(true);
      await loadWorkflowData({ silent: true, preserveExpanded: true });
      showSnackbar(
        response.data?.message || "Category scores saved successfully",
      );
    } catch (error) {
      showSnackbar(
        parseMiApiError(error, "Failed to save category scores"),
        "error",
      );
    } finally {
      setSavingKey("");
    }
  };

  const saveAllScores = async () => {
    const categoryIds = Object.keys(evaluationItems);
    const saveableCategories = categoryIds.filter((categoryId) =>
      (evaluationItems[categoryId] || []).some((item) =>
        itemHasScoreContent(scores[item.item_id], attachments[item.item_id]),
      ),
    );

    if (saveableCategories.length === 0) {
      showSnackbar("No score data available to save.", "warning");
      return;
    }

    try {
      setSavingKey("all-scores");
      for (const categoryId of saveableCategories) {
        const payload = buildMiBatchScorePayload(
          (evaluationItems[categoryId] || [])
            .filter((item) =>
              itemHasScoreContent(
                scores[item.item_id],
                attachments[item.item_id],
              ),
            )
            .map((item) => ({
              item_id: item.item_id,
              category_id: categoryId,
              score: scores[item.item_id] || {},
              attachments: attachments[item.item_id] || [],
            })),
        );

        await axios.post(
          `${API_URL}/api/mi/evaluations/${id}/scores/batch`,
          payload,
          {
            headers: {
              Authorization: localStorage.getItem("authKey") || "",
              "Content-Type": "multipart/form-data",
            },
          },
        );
      }

      await recalculateEvaluation(true);
      await loadWorkflowData({ silent: true, preserveExpanded: true });
      showSnackbar("All score sections saved successfully");
    } catch (error) {
      showSnackbar(
        parseMiApiError(error, "Failed to save all scores"),
        "error",
      );
    } finally {
      setSavingKey("");
    }
  };

  const saveNegativeMarking = async () => {
    try {
      setSavingKey("negative");
      const response = await axios.post(
        `${API_URL}/api/mi/evaluations/${id}/negative-marking`,
        {
          major_recommendations_date:
            negativeMarkingDraft.major_recommendations_date || null,
          total_1to2_years: Number(negativeMarkingDraft.total_1to2_years) || 0,
          complied_1to2_years:
            Number(negativeMarkingDraft.complied_1to2_years) || 0,
          total_gt2_years: Number(negativeMarkingDraft.total_gt2_years) || 0,
          complied_gt2_years:
            Number(negativeMarkingDraft.complied_gt2_years) || 0,
          remarks: (negativeMarkingDraft.remarks || "").trim() || null,
        },
        {
          headers: {
            Authorization: localStorage.getItem("authKey") || "",
          },
        },
      );

      await recalculateEvaluation(true);
      await loadWorkflowData({ silent: true, preserveExpanded: true });
      showSnackbar(
        response.data?.message || "Negative marking saved successfully",
      );
    } catch (error) {
      showSnackbar(
        parseMiApiError(error, "Failed to save negative marking"),
        "error",
      );
    } finally {
      setSavingKey("");
    }
  };

  const toggleChecklistValue = (field, selectedValue) => {
    setComplexityDraft((prevState) => {
      const currentValues = Array.isArray(prevState[field])
        ? prevState[field]
        : [];
      const exists = currentValues.includes(selectedValue);
      return {
        ...prevState,
        [field]: exists
          ? currentValues.filter((value) => value !== selectedValue)
          : [...currentValues, selectedValue],
      };
    });
  };

  const saveComplexityFactor = async () => {
    try {
      setSavingKey("complexity");
      const response = await axios.post(
        `${API_URL}/api/mi/evaluations/${id}/complexity-factor`,
        buildComplexityPayload(locationType, complexityDraft),
        {
          headers: {
            Authorization: localStorage.getItem("authKey") || "",
          },
        },
      );

      await recalculateEvaluation(true);
      await loadWorkflowData({ silent: true, preserveExpanded: true });
      showSnackbar(
        response.data?.message || "Complexity factor saved successfully",
      );
    } catch (error) {
      showSnackbar(
        parseMiApiError(error, "Failed to save complexity factor"),
        "error",
      );
    } finally {
      setSavingKey("");
    }
  };

  const updateEvaluationStatus = async (nextStatus) => {
    try {
      setSavingKey(`status-${nextStatus}`);
      await recalculateEvaluation(true);
      const response = await axios.put(
        `${API_URL}/api/mi/evaluations/${id}/status`,
        { status: nextStatus },
        {
          headers: {
            Authorization: localStorage.getItem("authKey") || "",
          },
        },
      );

      await loadWorkflowData({ silent: true, preserveExpanded: true });
      showSnackbar(response.data?.message || `Status updated to ${nextStatus}`);
    } catch (error) {
      showSnackbar(
        parseMiApiError(error, "Failed to update evaluation status"),
        "error",
      );
    } finally {
      setSavingKey("");
    }
  };

  const renderCategoryProgress = (categoryId) => {
    const items = evaluationItems[categoryId] || [];
    const scoredCount = items.filter((item) =>
      itemHasScoreContent(scores[item.item_id], attachments[item.item_id]),
    ).length;
    return `${scoredCount}/${items.length} items saved`;
  };

  if (loading) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <div>Loading MI workflow...</div>
      </div>
    );
  }

  return (
    <>
      <div
        className={`data-edit-form-content-container page-content mi-page ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-edit-form-content mi-page-layout">
          <div
            className="data-edit-form-header"
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            {/* <div className="data-edit-form-header-content"> */}
            <div className="data-edit-form-heading-content-left-side">
              <Tooltip title="Back" arrow>
                <IoMdArrowBack
                  className="back-company"
                  onClick={() => navigate("/mi-evaluations")}
                />
              </Tooltip>
              <span>MI Evaluation Workflow</span>
            </div>

            <div className="data-edit-form-heading-content-right-side mi-standard-header__actions">
              {/* {evaluationData.evaluation_id ? (
                  <span className="mi-id-badge">
                    ID: {evaluationData.evaluation_id}
                  </span>
                ) : null} */}
              <span className={`mi-pill mi-pill--${evaluationData.status}`}>
                {evaluationData.status}
              </span>
              <Link to={`/mi-evaluation-view/${id}`}>
                <button className="mi-btn mi-btn--secondary" type="button">
                  <FiEye /> View Page
                </button>
              </Link>
              <button
                className="mi-btn mi-btn--ghost"
                type="button"
                onClick={() => loadWorkflowData({ preserveExpanded: true })}
              >
                <FiRefreshCw /> Refresh
              </button>
            </div>
            {/* </div> */}

            {/* <div className="mi-standard-header__subtitle">
              Complete scoring, negative marking, complexity factor, and final
              approval from one API-driven workspace.
            </div> */}
          </div>
          <div className="header-separator"></div>

          <div className="data-edit-form-body">
            <div className="mi-shell">
              <div className="mi-stats">
                <div className="mi-card mi-stat">
                  <div className="mi-stat__label">Scoring Progress</div>
                  <div className="mi-stat__value">
                    {formatMiPercent(scoringProgress)}
                  </div>
                  <div className="mi-progress">
                    <div
                      className="mi-progress__fill"
                      style={{ width: `${scoringProgress}%` }}
                    ></div>
                  </div>
                  <div className="mi-stat__meta">
                    {scoredItemCount} of {totalItemCount} items captured
                  </div>
                </div>

                <div className="mi-card mi-stat">
                  <div className="mi-stat__label">Marks Obtained</div>
                  <div className="mi-stat__value">
                    {formatMiNumber(evaluationData.total_marks_obtained || 0)}
                  </div>
                  <div className="mi-stat__meta">
                    Out of{" "}
                    {formatMiNumber(evaluationData.total_max_marks || 97)}
                  </div>
                </div>

                <div className="mi-card mi-stat">
                  <div className="mi-stat__label">Complexity Factor</div>
                  <div className="mi-stat__value">
                    {formatMiNumber(evaluationData.complexity_factor || 0)}
                  </div>
                  <div className="mi-stat__meta">
                    Negative marks:{" "}
                    {formatMiNumber(
                      evaluationData.negative_marks_deducted || 0,
                    )}
                  </div>
                </div>

                <div className="mi-card mi-stat">
                  <div className="mi-stat__label">Final MI Score</div>
                  <div className="mi-stat__value">
                    {formatMiNumber(evaluationData.final_mi_score || 0)}
                  </div>
                  <div className="mi-stat__meta">
                    Percentage:{" "}
                    {formatMiPercent(evaluationData.percentage_obtained || 0)}
                  </div>
                </div>
              </div>

              <div className="mi-tabs">
                {[
                  ["overview", "Overview"],
                  ["scoring", "Scoring"],
                  ["negative", "Negative Marking"],
                  ["complexity", "Complexity Factor"],
                  ["finalize", "Finalize"],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    className={`mi-tab ${activeTab === key ? "is-active" : ""}`}
                    type="button"
                    onClick={() => setActiveTab(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {activeTab === "overview" && (
                <div className="mi-section mi-card">
                  <div className="mi-section__header">
                    <div>
                      <h2 className="mi-section__title">Evaluation Overview</h2>
                      <p className="mi-section__subtitle">
                        The current API supports creating evaluation header
                        data, then completing the workflow below. Header fields
                        are shown here for reference.
                      </p>
                    </div>
                  </div>

                  <div className="mi-readonly-grid">
                    <div className="mi-readonly">
                      <div className="mi-readonly__label">Location</div>
                      <div className="mi-readonly__value">
                        <strong>{evaluationData.location_name || "NA"}</strong>
                        <br />
                        {evaluationData.location_type || "NA"}
                        {evaluationData.region
                          ? ` - ${evaluationData.region}`
                          : ""}
                      </div>
                    </div>
                    <div className="mi-readonly">
                      <div className="mi-readonly__label">Evaluation Date</div>
                      <div className="mi-readonly__value">
                        {formatMiDate(evaluationData.evaluation_date)}
                      </div>
                    </div>
                    <div className="mi-readonly">
                      <div className="mi-readonly__label">Evaluator</div>
                      <div className="mi-readonly__value">
                        {evaluationData.evaluator_name || "NA"}
                        <br />
                        {evaluationData.evaluator_designation ||
                          "No designation"}
                      </div>
                    </div>
                    <div className="mi-readonly">
                      <div className="mi-readonly__label">Evaluation Type</div>
                      <div className="mi-readonly__value">
                        {evaluationData.evaluation_type || "NA"}
                      </div>
                    </div>
                    <div className="mi-readonly">
                      <div className="mi-readonly__label">Created At</div>
                      <div className="mi-readonly__value">
                        {formatMiDateTime(evaluationData.created_at)}
                      </div>
                    </div>
                    <div className="mi-readonly">
                      <div className="mi-readonly__label">Updated At</div>
                      <div className="mi-readonly__value">
                        {formatMiDateTime(evaluationData.updated_at)}
                      </div>
                    </div>
                    <div className="mi-readonly mi-field--span-2">
                      <div className="mi-readonly__label">Remarks</div>
                      <div className="mi-readonly__value">
                        {evaluationData.remarks || "No remarks added"}
                      </div>
                    </div>
                  </div>

                  {/* <div className="mi-divider"></div> */}

                  {/* <div className="mi-flow">
                    <div className="mi-flow__step">
                      <div className="mi-flow__step-index">1</div>
                      <div className="mi-flow__step-title">Save Scoring</div>
                      <div className="mi-flow__step-text">
                        Complete category-wise scoring, observations, and
                        supporting document uploads for each item.
                      </div>
                    </div>
                    <div className="mi-flow__step">
                      <div className="mi-flow__step-index">2</div>
                      <div className="mi-flow__step-title">
                        Add Deductions And Factors
                      </div>
                      <div className="mi-flow__step-text">
                        Submit negative marking and complexity factor after
                        scoring is complete so the summary uses the right
                        values.
                      </div>
                    </div>
                    <div className="mi-flow__step">
                      <div className="mi-flow__step-index">3</div>
                      <div className="mi-flow__step-title">
                        Recalculate And Move Status
                      </div>
                      <div className="mi-flow__step-text">
                        Recalculate the MI score, review the result, then send
                        the evaluation through approval stages.
                      </div>
                    </div>
                  </div> */}
                </div>
              )}

              {activeTab === "scoring" && (
                <div className="mi-section mi-card">
                  <div className="mi-section__header">
                    <div>
                      <h2 className="mi-section__title">Category Scoring</h2>
                      <p className="mi-section__subtitle">
                        Save one item, one category, or the full evaluation.
                        Uploaded attachments are tied to individual score rows.
                      </p>
                    </div>
                    <div className="mi-inline-actions">
                      <button
                        className="mi-btn mi-btn--secondary"
                        type="button"
                        onClick={() => recalculateEvaluation(false)}
                        disabled={savingKey !== "" || isApproved}
                      >
                        <FiRefreshCw />{" "}
                        {savingKey === "recalculate"
                          ? "Calculating..."
                          : "Recalculate"}
                      </button>
                      <button
                        className="mi-btn mi-btn--primary"
                        type="button"
                        onClick={saveAllScores}
                        disabled={savingKey !== "" || isApproved}
                      >
                        <FiSave />{" "}
                        {savingKey === "all-scores" ? "Saving..." : "Save All"}
                      </button>
                    </div>
                  </div>

                  <div className="mi-accordion">
                    {categories.map((category) => (
                      <div
                        key={category.category_id}
                        className="mi-accordion__item"
                      >
                        <button
                          className="mi-accordion__header"
                          type="button"
                          onClick={() => toggleCategory(category.category_id)}
                        >
                          <div className="mi-accordion__title">
                            <span className="mi-accordion__code">
                              {category.category_code}
                            </span>
                            <span className="mi-accordion__name">
                              {category.category_name}
                            </span>
                            <span className="mi-pill mi-pill--score">
                              Weight{" "}
                              {formatMiNumber(
                                category.weight_pct || category.max_marks || 0,
                              )}
                            </span>
                          </div>
                          <div style={{ color: "#64748b", fontWeight: 600 }}>
                            {renderCategoryProgress(category.category_id)}
                          </div>
                        </button>

                        {expandedCategories[category.category_id] && (
                          <div className="mi-accordion__body">
                            <div
                              className="mi-inline-actions"
                              style={{ marginBottom: 16 }}
                            >
                              <button
                                className="mi-btn mi-btn--secondary"
                                type="button"
                                onClick={() =>
                                  saveCategoryScores(category.category_id)
                                }
                                disabled={savingKey !== "" || isApproved}
                              >
                                <FiSave />{" "}
                                {savingKey ===
                                `category-${category.category_id}`
                                  ? "Saving..."
                                  : "Save Category"}
                              </button>
                            </div>

                            <div className="mi-items">
                              {(
                                evaluationItems[category.category_id] || []
                              ).map((item) => {
                                const currentScore = scores[item.item_id] || {};
                                const currentAttachments =
                                  attachments[item.item_id] || [];
                                const displayScore = getMiScoreValue({
                                  weighted_score: currentScore.allotted_mark,
                                  average_score: null,
                                  allotted_mark: currentScore.allotted_mark,
                                });

                                return (
                                  <div key={item.item_id} className="mi-item">
                                    <div className="mi-item__header">
                                      <div>
                                        <span className="mi-item__code">
                                          {item.item_code}
                                        </span>
                                        <span className="mi-item__description">
                                          {item.item_description}
                                        </span>
                                      </div>
                                      <div className="mi-inline-actions">
                                        <span className="mi-pill mi-pill--score">
                                          Max{" "}
                                          {formatMiNumber(item.max_marks || 0)}
                                        </span>
                                        {displayScore !== null && (
                                          <span className="mi-pill mi-pill--score">
                                            Draft {formatMiNumber(displayScore)}
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    <div className="mi-item__body">
                                      {item.has_subcategories &&
                                      Array.isArray(item.subcategory_names) &&
                                      item.subcategory_names.length > 0 ? (
                                        <div className="mi-grid-2">
                                          {item.subcategory_names.map(
                                            (subcategoryName) => (
                                              <div
                                                className="mi-field"
                                                key={subcategoryName}
                                              >
                                                <label>{subcategoryName}</label>
                                                <input
                                                  type="number"
                                                  min="0"
                                                  max={item.max_marks}
                                                  step="0.1"
                                                  value={
                                                    currentScore
                                                      .subcategory_scores?.[
                                                      subcategoryName
                                                    ] || ""
                                                  }
                                                  onChange={(event) =>
                                                    handleSubcategoryScoreChange(
                                                      item.item_id,
                                                      subcategoryName,
                                                      event.target.value,
                                                    )
                                                  }
                                                  disabled={isApproved}
                                                />
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      ) : (
                                        <div className="mi-grid-2">
                                          <div className="mi-field">
                                            <label>Allotted Mark</label>
                                            <input
                                              type="number"
                                              min="0"
                                              max={item.max_marks}
                                              step="0.1"
                                              value={
                                                currentScore.allotted_mark || ""
                                              }
                                              onChange={(event) =>
                                                handleScoreFieldChange(
                                                  item.item_id,
                                                  "allotted_mark",
                                                  event.target.value,
                                                )
                                              }
                                              disabled={isApproved}
                                              placeholder="Enter score"
                                            />
                                          </div>
                                        </div>
                                      )}

                                      <div className="mi-field">
                                        <label>Observations</label>
                                        <textarea
                                          value={
                                            currentScore.observations || ""
                                          }
                                          onChange={(event) =>
                                            handleScoreFieldChange(
                                              item.item_id,
                                              "observations",
                                              event.target.value,
                                            )
                                          }
                                          disabled={isApproved}
                                          placeholder="Add any scoring observations"
                                        />
                                      </div>

                                      <div className="mi-field">
                                        <label>Attachments</label>
                                        {!isApproved && (
                                          <div className="mi-inline-actions">
                                            <input
                                              ref={(element) => {
                                                fileInputRefs.current[
                                                  item.item_id
                                                ] = element;
                                              }}
                                              type="file"
                                              multiple
                                              style={{ display: "none" }}
                                              onChange={(event) =>
                                                handleFileSelection(
                                                  item.item_id,
                                                  event.target.files,
                                                )
                                              }
                                            />
                                            <button
                                              className="mi-btn mi-btn--secondary"
                                              type="button"
                                              onClick={() =>
                                                fileInputRefs.current[
                                                  item.item_id
                                                ]?.click()
                                              }
                                            >
                                              <FiUpload /> Upload Files
                                            </button>
                                          </div>
                                        )}

                                        {currentAttachments.length > 0 ? (
                                          <div className="mi-attachments">
                                            {currentAttachments.map(
                                              (attachment) => (
                                                <div
                                                  key={
                                                    attachment.id ||
                                                    attachment.client_id ||
                                                    getMiAttachmentName(
                                                      attachment,
                                                    )
                                                  }
                                                  className="mi-attachment"
                                                >
                                                  <div className="mi-attachment__meta">
                                                    <div className="mi-attachment__name">
                                                      {getMiAttachmentName(
                                                        attachment,
                                                      )}
                                                    </div>
                                                    <div className="mi-attachment__sub">
                                                      {getMiAttachmentSize(
                                                        attachment,
                                                      )}
                                                      {attachment.uploaded_at
                                                        ? ` | ${attachment.uploaded_at}`
                                                        : attachment.isNew
                                                          ? " | Ready to upload"
                                                          : ""}
                                                    </div>
                                                  </div>
                                                  <div className="mi-attachment__actions">
                                                    {!attachment.isNew && (
                                                      <>
                                                        <button
                                                          className="mi-btn mi-btn--secondary"
                                                          type="button"
                                                          onClick={() =>
                                                            openMiAttachment(
                                                              attachment.id,
                                                            )
                                                          }
                                                        >
                                                          <FiEye /> View
                                                        </button>
                                                        <button
                                                          className="mi-btn mi-btn--secondary"
                                                          type="button"
                                                          onClick={() =>
                                                            downloadMiAttachment(
                                                              attachment.id,
                                                              getMiAttachmentName(
                                                                attachment,
                                                              ),
                                                            )
                                                          }
                                                        >
                                                          <FiDownload />{" "}
                                                          Download
                                                        </button>
                                                      </>
                                                    )}
                                                    {!isApproved && (
                                                      <button
                                                        className="mi-btn mi-btn--ghost"
                                                        type="button"
                                                        onClick={() =>
                                                          handleRemoveAttachment(
                                                            item.item_id,
                                                            attachment,
                                                          )
                                                        }
                                                        disabled={
                                                          savingKey ===
                                                          `delete-attachment-${attachment.id}`
                                                        }
                                                      >
                                                        <FiTrash2 /> Remove
                                                      </button>
                                                    )}
                                                  </div>
                                                </div>
                                              ),
                                            )}
                                          </div>
                                        ) : (
                                          <div className="mi-empty">
                                            No attachments added for this item
                                            yet.
                                          </div>
                                        )}
                                      </div>

                                      {!isApproved && (
                                        <div className="mi-inline-actions">
                                          <button
                                            className="mi-btn mi-btn--primary"
                                            type="button"
                                            onClick={() =>
                                              saveItemScore(
                                                item.item_id,
                                                category.category_id,
                                              )
                                            }
                                            disabled={
                                              savingKey !== "" &&
                                              savingKey !==
                                                `item-${item.item_id}`
                                            }
                                          >
                                            <FiSave />{" "}
                                            {savingKey ===
                                            `item-${item.item_id}`
                                              ? "Saving..."
                                              : "Save Item"}
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === "negative" && (
                <div className="mi-section mi-card">
                  <div className="mi-section__header">
                    <div>
                      <h2 className="mi-section__title">Negative Marking</h2>
                      <p className="mi-section__subtitle">
                        Submit the band-wise pending/complied recommendation
                        counts expected by the negative-marking API.
                      </p>
                    </div>
                    <button
                      className="mi-btn mi-btn--primary"
                      type="button"
                      onClick={saveNegativeMarking}
                      disabled={savingKey !== "" || isApproved}
                    >
                      <FiSave />{" "}
                      {savingKey === "negative"
                        ? "Saving..."
                        : "Save Negative Marking"}
                    </button>
                  </div>

                  {/* {negativeMarkingSnapshot && (
                    <div
                      className="mi-note mi-warning"
                      style={{ marginBottom: 16 }}
                    >
                      The API returns the saved summary fields, but it does not
                      return the original band-wise complied counts separately.
                      If you need to update an existing negative-marking record,
                      re-enter the counts below before saving again.
                    </div>
                  )} */}

                  <div className="mi-toolbar__grid">
                    <div className="mi-field">
                      <label>Major Recommendations Date</label>
                      <input
                        type="date"
                        value={negativeMarkingDraft.major_recommendations_date}
                        onChange={(event) =>
                          setNegativeMarkingDraft((prevState) => ({
                            ...prevState,
                            major_recommendations_date: event.target.value,
                          }))
                        }
                        disabled={isApproved}
                      />
                    </div>

                    <div className="mi-field">
                      <label>Total &gt;1yr and &lt;2yr</label>
                      <input
                        type="number"
                        min="0"
                        value={negativeMarkingDraft.total_1to2_years}
                        onChange={(event) =>
                          setNegativeMarkingDraft((prevState) => ({
                            ...prevState,
                            total_1to2_years: event.target.value,
                          }))
                        }
                        disabled={isApproved}
                      />
                    </div>

                    <div className="mi-field">
                      <label>Complied &gt;1yr and &lt;2yr</label>
                      <input
                        type="number"
                        min="0"
                        value={negativeMarkingDraft.complied_1to2_years}
                        onChange={(event) =>
                          setNegativeMarkingDraft((prevState) => ({
                            ...prevState,
                            complied_1to2_years: event.target.value,
                          }))
                        }
                        disabled={isApproved}
                      />
                    </div>

                    <div className="mi-field">
                      <label>Total &gt;2yr</label>
                      <input
                        type="number"
                        min="0"
                        value={negativeMarkingDraft.total_gt2_years}
                        onChange={(event) =>
                          setNegativeMarkingDraft((prevState) => ({
                            ...prevState,
                            total_gt2_years: event.target.value,
                          }))
                        }
                        disabled={isApproved}
                      />
                    </div>

                    <div className="mi-field">
                      <label>Complied &gt;2yr</label>
                      <input
                        type="number"
                        min="0"
                        value={negativeMarkingDraft.complied_gt2_years}
                        onChange={(event) =>
                          setNegativeMarkingDraft((prevState) => ({
                            ...prevState,
                            complied_gt2_years: event.target.value,
                          }))
                        }
                        disabled={isApproved}
                      />
                    </div>

                    <div className="mi-field mi-field--span-4">
                      <label>Remarks</label>
                      <textarea
                        value={negativeMarkingDraft.remarks}
                        onChange={(event) =>
                          setNegativeMarkingDraft((prevState) => ({
                            ...prevState,
                            remarks: event.target.value,
                          }))
                        }
                        disabled={isApproved}
                        placeholder="Add optional remarks"
                      />
                    </div>
                  </div>

                  <div className="mi-divider"></div>

                  <div className="mi-report-stats">
                    <div className="mi-card mi-stat">
                      <div className="mi-stat__label">1 To 2 Year NC%</div>
                      <div className="mi-stat__value">
                        {formatMiPercent(negativePreview.nc1To2Percent)}
                      </div>
                      <div className="mi-stat__meta">
                        Pending count: {negativePreview.nc1To2Count}
                      </div>
                    </div>

                    <div className="mi-card mi-stat">
                      <div className="mi-stat__label">&gt;2 Year NC%</div>
                      <div className="mi-stat__value">
                        {formatMiPercent(negativePreview.ncGt2Percent)}
                      </div>
                      <div className="mi-stat__meta">
                        Pending count: {negativePreview.ncGt2Count}
                      </div>
                    </div>

                    <div className="mi-card mi-stat">
                      <div className="mi-stat__label">Negative Marks</div>
                      <div className="mi-stat__value">
                        {formatMiNumber(negativePreview.totalNegativeMarks)}
                      </div>
                      <div className="mi-stat__meta">
                        1-2yr: {formatMiNumber(negativePreview.mark1To2)} |
                        &gt;2yr: {formatMiNumber(negativePreview.markGt2)}
                      </div>
                    </div>

                    <div className="mi-card mi-stat">
                      <div className="mi-stat__label">Applied Deduction</div>
                      <div className="mi-stat__value">
                        {formatMiNumber(
                          negativePreview.actualNegativeMarksDeducted,
                        )}
                      </div>
                      <div className="mi-stat__meta">
                        Capped at -2.00 by API
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "complexity" && (
                <div className="mi-section mi-card">
                  <div className="mi-section__header">
                    <div>
                      <h2 className="mi-section__title">Complexity Factor</h2>
                      <p className="mi-section__subtitle">
                        {locationType === "AFS"
                          ? "AFS scoring uses sales volume, zone, age, and customer mix."
                          : "Depot and Terminal scoring uses capacity, zone, age, and operations mix."}
                      </p>
                    </div>
                    <button
                      className="mi-btn mi-btn--primary"
                      type="button"
                      onClick={saveComplexityFactor}
                      disabled={savingKey !== "" || isApproved}
                    >
                      <FiSave />{" "}
                      {savingKey === "complexity"
                        ? "Saving..."
                        : "Save Complexity Factor"}
                    </button>
                  </div>

                  <div className="mi-toolbar__grid">
                    {locationType === "AFS" ? (
                      <div className="mi-field">
                        <label>Sales Volume</label>
                        <select
                          value={complexityDraft.sales_volume}
                          onChange={(event) =>
                            setComplexityDraft((prevState) => ({
                              ...prevState,
                              sales_volume: event.target.value,
                            }))
                          }
                          disabled={isApproved}
                        >
                          <option value="">Select sales volume</option>
                          {MI_AFS_SALES_VOLUME_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="mi-field">
                        <label>Capacity</label>
                        <select
                          value={complexityDraft.capacity}
                          onChange={(event) =>
                            setComplexityDraft((prevState) => ({
                              ...prevState,
                              capacity: event.target.value,
                            }))
                          }
                          disabled={isApproved}
                        >
                          <option value="">Select capacity</option>
                          {MI_DEPOT_CAPACITY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className="mi-field">
                      <label>Zone</label>
                      <select
                        value={complexityDraft.zone}
                        onChange={(event) =>
                          setComplexityDraft((prevState) => ({
                            ...prevState,
                            zone: event.target.value,
                          }))
                        }
                        disabled={isApproved}
                      >
                        <option value="">Select zone</option>
                        {MI_ZONE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="mi-field">
                      <label>Installation Age</label>
                      <select
                        value={complexityDraft.age_over_25 ? "yes" : "no"}
                        onChange={(event) =>
                          setComplexityDraft((prevState) => ({
                            ...prevState,
                            age_over_25: event.target.value === "yes",
                          }))
                        }
                        disabled={isApproved}
                      >
                        <option value="no">25 years or less</option>
                        <option value="yes">Over 25 years</option>
                      </select>
                    </div>

                    <div className="mi-field mi-field--span-4">
                      <label>
                        {locationType === "AFS" ? "Customers" : "Operations"}
                      </label>
                      <div className="mi-check-grid">
                        {(locationType === "AFS"
                          ? MI_AFS_CUSTOMER_OPTIONS
                          : MI_DEPOT_OPERATION_OPTIONS
                        ).map((option) => {
                          const selectedValues =
                            locationType === "AFS"
                              ? complexityDraft.customers
                              : complexityDraft.operations;
                          return (
                            <label className="mi-check" key={option.value}>
                              <input
                                type="checkbox"
                                checked={(selectedValues || []).includes(
                                  option.value,
                                )}
                                onChange={() =>
                                  toggleChecklistValue(
                                    locationType === "AFS"
                                      ? "customers"
                                      : "operations",
                                    option.value,
                                  )
                                }
                                disabled={isApproved}
                              />
                              <span>{option.label}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div className="mi-field mi-field--span-4">
                      <label>Remarks</label>
                      <textarea
                        value={complexityDraft.remarks}
                        onChange={(event) =>
                          setComplexityDraft((prevState) => ({
                            ...prevState,
                            remarks: event.target.value,
                          }))
                        }
                        disabled={isApproved}
                        placeholder="Add optional remarks"
                      />
                    </div>
                  </div>

                  <div className="mi-divider"></div>

                  <div className="mi-report-stats">
                    <div className="mi-card mi-stat">
                      <div className="mi-stat__label">W Weight</div>
                      <div className="mi-stat__value">
                        {formatMiNumber(complexityPreview.W)}
                      </div>
                      <div className="mi-stat__meta">
                        {locationType === "AFS" ? "Sales volume" : "Capacity"}
                      </div>
                    </div>
                    <div className="mi-card mi-stat">
                      <div className="mi-stat__label">P Weight</div>
                      <div className="mi-stat__value">
                        {formatMiNumber(complexityPreview.P)}
                      </div>
                      <div className="mi-stat__meta">Zone factor</div>
                    </div>
                    <div className="mi-card mi-stat">
                      <div className="mi-stat__label">A Weight</div>
                      <div className="mi-stat__value">
                        {formatMiNumber(complexityPreview.A)}
                      </div>
                      <div className="mi-stat__meta">Age factor</div>
                    </div>
                    <div className="mi-card mi-stat">
                      <div className="mi-stat__label">C Weight</div>
                      <div className="mi-stat__value">
                        {formatMiNumber(complexityPreview.C)}
                      </div>
                      <div className="mi-stat__meta">
                        {locationType === "AFS"
                          ? "Customer mix"
                          : "Operations mix"}
                      </div>
                    </div>
                  </div>

                  <div className="mi-note" style={{ marginTop: 16 }}>
                    Previewed complexity factor:{" "}
                    <strong>
                      {formatMiNumber(complexityPreview.total, 4)}
                    </strong>
                  </div>
                </div>
              )}

              {activeTab === "finalize" && (
                <div className="mi-section mi-card">
                  <div className="mi-section__header">
                    <div>
                      <h2 className="mi-section__title">Finalize Evaluation</h2>
                      <p className="mi-section__subtitle">
                        Recalculate the score, then move the evaluation through
                        Draft, Submitted, Reviewed, and Approved.
                      </p>
                    </div>
                  </div>

                  <div className="mi-readonly-grid">
                    <div className="mi-readonly">
                      <div className="mi-readonly__label">Current Status</div>
                      <div className="mi-readonly__value">
                        <span
                          className={`mi-pill mi-pill--${evaluationData.status}`}
                        >
                          {evaluationData.status}
                        </span>
                      </div>
                    </div>
                    <div className="mi-readonly">
                      <div className="mi-readonly__label">Final Score</div>
                      <div className="mi-readonly__value">
                        {formatMiNumber(evaluationData.final_mi_score || 0)}
                      </div>
                    </div>
                    <div className="mi-readonly">
                      <div className="mi-readonly__label">Percentage</div>
                      <div className="mi-readonly__value">
                        {formatMiPercent(
                          evaluationData.percentage_obtained || 0,
                        )}
                      </div>
                    </div>
                    <div className="mi-readonly">
                      <div className="mi-readonly__label">
                        Complexity Minus Negative
                      </div>
                      <div className="mi-readonly__value">
                        {formatMiNumber(evaluationData.complexity_factor || 0)}{" "}
                        -{" "}
                        {formatMiNumber(
                          evaluationData.negative_marks_deducted || 0,
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mi-divider"></div>

                  <div className="mi-inline-actions">
                    <button
                      className="mi-btn mi-btn--secondary"
                      type="button"
                      onClick={() => recalculateEvaluation(false)}
                      disabled={savingKey !== "" || isApproved}
                    >
                      <FiRefreshCw />{" "}
                      {savingKey === "recalculate"
                        ? "Calculating..."
                        : "Recalculate Score"}
                    </button>

                    {evaluationData.status === "Draft" && (
                      <button
                        className="mi-btn mi-btn--primary"
                        type="button"
                        onClick={() => updateEvaluationStatus("Submitted")}
                        disabled={savingKey !== ""}
                      >
                        <FiCheckCircle />{" "}
                        {savingKey === "status-Submitted"
                          ? "Submitting..."
                          : "Submit For Review"}
                      </button>
                    )}

                    {evaluationData.status === "Submitted" && (
                      <>
                        <button
                          className="mi-btn mi-btn--primary"
                          type="button"
                          onClick={() => updateEvaluationStatus("Reviewed")}
                          disabled={savingKey !== ""}
                        >
                          <FiCheckCircle />{" "}
                          {savingKey === "status-Reviewed"
                            ? "Updating..."
                            : "Mark As Reviewed"}
                        </button>
                        <button
                          className="mi-btn mi-btn--warning"
                          type="button"
                          onClick={() => updateEvaluationStatus("Draft")}
                          disabled={savingKey !== ""}
                        >
                          Send Back To Draft
                        </button>
                      </>
                    )}

                    {evaluationData.status === "Reviewed" && (
                      <>
                        <button
                          className="mi-btn mi-btn--success"
                          type="button"
                          onClick={() => updateEvaluationStatus("Approved")}
                          disabled={savingKey !== ""}
                        >
                          <FiCheckCircle />{" "}
                          {savingKey === "status-Approved"
                            ? "Approving..."
                            : "Approve Evaluation"}
                        </button>
                        <button
                          className="mi-btn mi-btn--warning"
                          type="button"
                          onClick={() => updateEvaluationStatus("Submitted")}
                          disabled={savingKey !== ""}
                        >
                          Send Back To Submitted
                        </button>
                      </>
                    )}

                    <Link to="/mi-reports">
                      <button className="mi-btn mi-btn--ghost" type="button">
                        Open Reports
                      </button>
                    </Link>
                  </div>
                </div>
              )}

              {/* <div className="footer">
            <div className="button-group">
              <button
                className="data-edit-form-cancel-button"
                type="button"
                onClick={() => navigate("/mi-evaluations")}
              >
                Back To List
              </button>
            </div>
          </div> */}
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default EditMIEvaluation;
